import { Box, Button, Icon, Theme, Typography } from "@outschool/backpack";
import { ClassDetailsQuery_ActivityFragment } from "@outschool/gql-frontend-generated";
import {
  faEnvelope,
  faFire,
  faRoad,
  fasCalendar,
  fasMessageLines,
  fasPlayPause,
} from "@outschool/icons";
import { Trans, useTranslation } from "@outschool/localization";
import { addParamsToUrl } from "@outschool/routes";
import { useTrackEvent } from "@outschool/ui-analytics";
import {
  AuthTrigger,
  useLoginFlowContext,
  useSession,
} from "@outschool/ui-auth";
import { pxToRem, useIsMobile } from "@outschool/ui-utils";
import React from "react";

import backgroundImage from "../../images/SP_banner_corner image.png";
import HelpLink from "../HelpLink";
import { SectionCallToActionProps } from "./AvailableTimes/types";
import HeadingMarker from "./HeadingMarker";

export default function SelfPacedAvailableNow({
  activity,
  SectionCallToAction,
  MessageModal,
}: {
  activity: ClassDetailsQuery_ActivityFragment;
  SectionCallToAction: React.FC<SectionCallToActionProps>;
  MessageModal: React.ElementType;
}) {
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\SelfPacedAvailableNow"
  );
  const isMobile = useIsMobile();

  return (
    <Box>
      <Box
        sx={{
          paddingBottom: "1em",
        }}
      >
        <Box mt={32}>
          <HeadingMarker />
        </Box>
        <Typography
          variant="h4"
          sx={(theme: Theme) => ({
            fontSize: theme.typography.pxToRem(24),
            fontWeight: 700,
            [theme.breakpoints.up("md")]: {
              display: "block",
            },
          })}
        >
          {t`Enroll Now, Start Anytime`}
        </Typography>
      </Box>
      {activity.activeSections && activity.activeSections > 10 ? (
        <Box
          flex
          sx={{
            paddingBottom: "1em",
            flexDirection: isMobile ? "column" : "row",
          }}
        >
          <Box
            flex
            sx={{
              paddingBottom: isMobile ? pxToRem(8) : pxToRem(0),
            }}
          >
            <Icon
              icon={faFire}
              size={"small"}
              sx={{
                color: "error.600",
              }}
            />
            <Icon
              icon={faFire}
              size={"small"}
              sx={{
                color: "error.600",
              }}
            />
            <Icon
              icon={faFire}
              size={"small"}
              sx={{
                color: "error.600",
              }}
            />
          </Box>
          <Typography
            variant="subtitle1"
            sx={{
              paddingLeft: isMobile ? pxToRem(0) : pxToRem(8),
              color: "error.600",
            }}
          >{t`This class is popular! Over 10 learners are currently taking this class!`}</Typography>
        </Box>
      ) : null}
      <Box
        sx={(theme: Theme) => {
          return {
            padding: pxToRem(32),
            borderRadius: pxToRem(32),
            // @ts-ignore
            backgroundColor: theme.palette.primary["50"],
            backgroundImage: `url('${backgroundImage}')`,
            backgroundPosition: "right bottom",
            backgroundRepeat: "no-repeat",
            [theme.breakpoints.down("md")]: {
              padding: pxToRem(24),
              borderRadius: pxToRem(24),
            },
          };
        }}
      >
        <Box
          flex
          sx={(theme: Theme) => ({
            paddingBottom: pxToRem(16),
            gap: pxToRem(32),
            [theme.breakpoints.down("md")]: {
              gap: pxToRem(24),
            },
            flexDirection: isMobile ? "column" : "row",
          })}
        >
          <Box>
            <Typography
              emphasized
              variant="h5"
              sx={{ paddingBottom: pxToRem(8) }}
            >{t`Go at your own pace`}</Typography>
            <Typography variant="body1">{t`Your learner can start learning when they want and progress at the speed that works best for them.`}</Typography>
          </Box>
          <Box
            flex
            sx={{
              flexDirection: "column",
              alignItems: "center",
              paddingBottom: isMobile ? pxToRem(8) : pxToRem(0),
            }}
          >
            <SectionCallToAction
              activity={activity}
              parentUid={undefined}
              variant="contained"
              section={null}
              isOrganizationEnrollment={false}
              size="large"
              trackingSharedProperties={{
                component: "SelfPacedAvailableNow",
              }}
            />
            {!activity.canEnroll && (
              <Box sx={{ padding: 8 }}>
                <MessageTeacher
                  MessageModal={MessageModal}
                  activity={activity}
                />
              </Box>
            )}
          </Box>
        </Box>
        <Box
          flex
          sx={{ flexDirection: "column", textWrap: "nowrap", gap: pxToRem(16) }}
        >
          <Box flex sx={{ flexWrap: "wrap", gap: pxToRem(16) }}>
            <Box flex sx={{ alignItems: "center" }}>
              <Icon
                icon={fasPlayPause}
                color="warning"
                size={"medium"}
                sx={{
                  paddingRight: pxToRem(8),
                }}
              />
              <Typography variant="subtitle1">
                {t`Start when you want.`}
              </Typography>
            </Box>
            <Box flex sx={{ alignItems: "center" }}>
              <Icon
                icon={faRoad}
                color="secondary"
                size={"medium"}
                sx={{
                  paddingRight: pxToRem(8),
                }}
              />
              <Typography variant="subtitle1">
                {t`Learn at your own pace`}
              </Typography>
            </Box>
          </Box>
          <Box flex sx={{ flexWrap: "wrap", gap: pxToRem(16) }}>
            <Box flex sx={{ alignItems: "center" }}>
              <Icon
                icon={fasCalendar}
                color="info"
                size={"medium"}
                sx={{
                  paddingRight: pxToRem(8),
                }}
              />
              <Typography variant="subtitle1">
                {t`Access course material for a full year.`}
              </Typography>
            </Box>
            <Box flex sx={{ alignItems: "center" }}>
              <Icon
                icon={fasMessageLines}
                color="error"
                size={"medium"}
                sx={{
                  paddingRight: pxToRem(8),
                }}
              />
              <Typography variant="subtitle1">
                {t`Get hands-on support from teacher.`}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

function MessageTeacher({
  MessageModal,
  activity,
}: {
  MessageModal: React.ElementType;
  activity: ClassDetailsQuery_ActivityFragment;
}) {
  const { isLoggedIn, currentUser } = useSession();
  const { t } = useTranslation(
    "ui-components-website\\ClassDetailsPage\\SelfPacedAvailableNow"
  );
  const trackEvent = useTrackEvent();
  const { enterLoginFlow } = useLoginFlowContext();
  const leader = activity.leader;

  if (isLoggedIn && !!MessageModal) {
    return (
      <MessageModal
        renderButton={(open: () => void) => (
          <Button
            size="small"
            variant="text"
            onClick={open}
            startIcon={<Icon icon={faEnvelope} />}
          >
            {t`Message the Teacher`}
          </Button>
        )}
        user={currentUser}
        requireLogin={true}
        isLoggedIn={isLoggedIn}
        viewName="messageSelfPaced"
        heading={t("Ask the teacher")}
        prompt={(close: () => void) => (
          <Trans t={t}>
            For example, ask about class details or logistics. For general
            questions about Outschool, you can{" "}
            <HelpLink sx={{ width: "fit-content" }} onClick={close}>
              contact Outschool directly
            </HelpLink>
            .
          </Trans>
        )}
        showFromAndTo={true}
        subject={"Message about: " + activity?.title}
        recipients={[leader]}
        activityUid={activity?.uid}
        onMessageCreated={() => {
          trackEvent("Clicked Ask a Question on Self-Paced", {
            activityUid: activity.uid,
            leaderUid: leader.uid,
          });
        }}
        context={{
          action: "Ask a Question",
          activityTitle: activity?.title,
          subject: "Message about: " + activity?.title,
        }}
      />
    );
  } else {
    return (
      <Button
        size="small"
        variant="text"
        startIcon={<Icon icon={faEnvelope} />}
        sx={{ paddingX: 8, flex: "1 1 auto" }}
        onClick={() => {
          const location = window.location;
          trackEvent("Clicked Ask a Question", {
            leaderUid: leader.uid,
          });
          enterLoginFlow({
            authTrigger: AuthTrigger.MESSAGE_TEACHER,
            newUrl: addParamsToUrl(location.pathname + location.search, {
              openMB: "messageSelfPaced",
            }),
          });
        }}
      >
        {t`Message the Teacher`}
      </Button>
    );
  }
}
